import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ZoneSelector } from '@/features/quiz/ZoneSelector.jsx';
import { recordZone } from '@/api/profiling.js';

// Overlay non-bloquant de capture de la zone (écran de résultat, post-match)
const ZoneProfilingOverlay = ({ isOpen, contestId, zones = [], onClose }) => {
    const { t } = useTranslation();
    const [zone, setZone] = useState(null);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!zone || saving) return;
        setSaving(true);
        try {
            await recordZone(zone, contestId);
        } catch (e) {
            console.warn('recordZone failed', e);
        } finally {
            setSaving(false);
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                >
                    {/* Backdrop non cliquable : la sélection d'une zone est obligatoire pour continuer. */}
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />

                    <motion.div
                        initial={{ y: 60, opacity: 0, scale: 0.96 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ type: 'spring', damping: 22, stiffness: 260 }}
                        className="relative z-10 w-full max-w-sm bg-brand-dark rounded-3xl p-7 overflow-hidden shadow-2xl"
                    >
                        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-action-green/20 blur-[70px] pointer-events-none" />

                        <div className="relative z-10">
                            <div className="text-center mb-6">
                                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-action-green/15 border border-action-green/30 flex items-center justify-center text-2xl">
                                    📍
                                </div>
                                <h3 className="text-xl font-black text-white uppercase tracking-tight leading-none mb-2">
                                    {t('profiling.overlay.title', 'Une dernière chose')}
                                </h3>
                                <p className="text-sm text-gray-400 font-medium leading-relaxed">
                                    {t('profiling.overlay.subtitle', "Aide ton bar à mieux te connaître : d'où viens-tu ?")}
                                </p>
                            </div>

                            <ZoneSelector zones={zones} value={zone} onSelect={setZone} />

                            <button
                                onClick={submit}
                                disabled={!zone || saving}
                                className={`w-full mt-6 py-4 font-black text-base uppercase tracking-widest rounded-xl transition-all ${
                                    !zone || saving
                                        ? 'bg-white/10 text-gray-500 cursor-not-allowed'
                                        : 'bg-action-green text-white hover:scale-[1.02] active:scale-95 shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]'
                                }`}
                            >
                                {saving ? t('profiling.overlay.saving', 'Enregistrement...') : t('profiling.overlay.cta', 'Valider')}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ZoneProfilingOverlay;
